import { ImgID } from "../../../image_loader/img_id.js";
import { OfferService } from "../../../offers/offer_service.js";
import { TonPaymentPending } from "../../../plugins/ton_payments.js";
import { offerButtonLabel, offerLocalizedText } from "../../../offers/offer_visual_registry.js";
import { showRewarded } from "../../../ads/rewarded.js";
import { formatMinutesShort } from "../../../common/reward_format.js";
import type { ClaimFreeOfferResponse, OfferDto, OfferRewardDto, ValidatePurchaseResponse } from "../../../api/dto/offers.dto.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../../api/player_sync.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";

export const CONTINUE_SPECIAL_OFFER_PLACEMENT = "continue_dialog";
export const CONTINUE_SPECIAL_OFFER_TEMPLATE = "continue_special_offer";

export interface ContinueSpecialOfferPanelParams
{
  offer: OfferDto;
  /** Decision that opened this placement; absent when it came from the catalog. */
  trigger_event_id?: string;
  /** Called with the granted level shots (0 when the bundle carries none). */
  on_purchased: ( level_shots: number ) => void;
}

/**
 * The "special offer" strip under the Continue dialog: title tab, one line of
 * copy, the reward row and the price button. Everything but the layout comes
 * from the placement offer — texts, rewards, purchase mode, price.
 */
export class ContinueSpecialOfferPanel extends UIElementHolder
{
  public static readonly DESIGN_W = 909;
  public static readonly DESIGN_H = 360;
  private static readonly FREE_BUTTON_HUE_ROTATION = 160;

  private price_text: UITextElement | null = null;
  private purchase_in_progress = false;
  private purchased = false;
  private close_tracked = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    private readonly params: ContinueSpecialOfferPanelParams
  )
  {
    super( ctx, x, y, LayoutScaler.widthFromDesign( ContinueSpecialOfferPanel.DESIGN_W ), LayoutScaler.heightFromDesign( ContinueSpecialOfferPanel.DESIGN_H ) );
    this.createBackground();
    this.createTitleTab();
    this.createDescription();
    this.createRewardsAndButton();
    void this.loadPrice();
    // The panel is both the entry point becoming visible and the offer being
    // shown, so it reports the same pair the lobby button + dialog do.
    void OfferService.trackPlacementImpression( params.offer.id, this.analyticsContext() );
    void OfferService.trackShown( params.offer.id, this.analyticsContext() );
  }

  /** Reported once by the overlay when it goes away without this offer being bought. */
  public trackClosed( reason: string ): void
  {
    if ( this.purchased || this.close_tracked ) return;
    this.close_tracked = true;
    void OfferService.trackClosed( this.params.offer.id, this.analyticsContext( { reason } ) );
  }

  public name(): string { return "ContinueSpecialOfferPanel"; }

  private analyticsContext( extra: Record<string, unknown> = {} ): Record<string, unknown>
  {
    return {
      placement: CONTINUE_SPECIAL_OFFER_PLACEMENT,
      placement_id: CONTINUE_SPECIAL_OFFER_PLACEMENT,
      template: CONTINUE_SPECIAL_OFFER_TEMPLATE,
      ...( this.params.trigger_event_id ? { trigger_event_id: this.params.trigger_event_id } : {} ),
      ...extra,
    };
  }

  private createBackground(): void
  {
    const top = LayoutScaler.yFromDesign( 40 );
    const w = this.rect.getW();
    const h = this.rect.getH() - LayoutScaler.heightFromDesign( 40 );
    const border = LayoutScaler.widthFromDesign( 8 );
    const frame = new RoundedRectangle( this.ctx, 0, top, w, h, LayoutScaler.cornerRadiusFromDesign( 56 ), {
      type: 'linear-gradient', direction: 'vertical',
      stops: [ { offset: 0, color: '#2E63D6' }, { offset: 1, color: '#173F9F' } ],
    }, undefined, false, '#F5A623', border, {
      color: 'rgba(0, 0, 0, 0.35)', blur: LayoutScaler.widthFromDesign( 10 ), offsetY: LayoutScaler.yFromDesign( 4 ),
    } );
    this.addChild( frame );
    const inset = border + LayoutScaler.widthFromDesign( 6 );
    this.addChild( new RoundedRectangle( this.ctx, inset, top + inset, w - inset * 2, h - inset * 2,
      LayoutScaler.cornerRadiusFromDesign( 44 ), 'rgba(0, 0, 0, 0)', undefined, false, 'rgba(120, 170, 255, 0.55)', LayoutScaler.widthFromDesign( 3 ) ) );
  }

  private createTitleTab(): void
  {
    const tab_w = LayoutScaler.widthFromDesign( 640 );
    const tab_h = LayoutScaler.heightFromDesign( 92 );
    const tab_x = ( this.rect.getW() - tab_w ) / 2;
    const tab = new RoundedRectangle( this.ctx, tab_x, 0, tab_w, tab_h, LayoutScaler.cornerRadiusFromDesign( 40 ), {
      type: 'linear-gradient', direction: 'vertical',
      stops: [ { offset: 0, color: '#1E5FD8' }, { offset: 1, color: '#0B3A9C' } ],
    } );
    this.addChild( tab );
    tab.addChild( new UITextElement( this.ctx, 0, 0, tab_w, tab_h, {
      text: offerLocalizedText( this.params.offer.texts?.title_key, "OFFER.CONTINUE.TITLE", "Special offer" ),
      font_size: LayoutScaler.textSizeFromDesign( 48 ), font_weight: 900,
      text_style: TextStyleScheme.rubik, text_color: ColorScheme.white,
      text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ), outline_color: '#073985',
      auto_wrap: false, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
    } ) );
  }

  private createDescription(): void
  {
    const shots = this.params.offer.rewards?.level_shots ?? 0;
    const description_key = this.params.offer.texts?.description_key;
    this.addChild( new UITextElement(
      this.ctx, LayoutScaler.xFromDesign( 40 ), LayoutScaler.yFromDesign( 104 ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 80 ), LayoutScaler.heightFromDesign( 72 ),
      {
        text: offerLocalizedText( description_key, "OFFER.CONTINUE.PLUS_SHOTS", "+{0} shots for this level", [ String( shots ) ] ),
        font_size: LayoutScaler.textSizeFromDesign( 44 ), font_weight: 900,
        text_style: TextStyleScheme.rubik, text_color: ColorScheme.white,
        text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ), outline_color: '#073985',
        auto_wrap: false, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 26 ),
      }
    ) );
  }

  private getRewards(): OfferRewardDto[]
  {
    const raw = this.params.offer.rewards ?? {};
    return Object.entries( raw ).flatMap( ( [ type, amount ] ) =>
    {
      // if ( type === 'no_ads' && amount === true ) return [ { type: 'no_ads' as const, amount: 1 } ];
      return typeof amount === 'number' && amount > 0 ? [ { type: type as OfferRewardDto['type'], amount } ] : [];
    } );
  }

  private createRewardsAndButton(): void
  {
    // Dark strip with the reward icons on the left and the price on the right.
    const strip_x = LayoutScaler.xFromDesign( 40 );
    const strip_y = LayoutScaler.yFromDesign( 186 );
    const strip_w = this.rect.getW() - LayoutScaler.widthFromDesign( 80 );
    const strip_h = LayoutScaler.heightFromDesign( 150 );
    const strip = new RoundedRectangle( this.ctx, strip_x, strip_y, strip_w, strip_h, LayoutScaler.cornerRadiusFromDesign( 30 ), '#0B3A9C' );
    this.addChild( strip );

    const button_w = LayoutScaler.widthFromDesign( 300 );
    const button_h = LayoutScaler.heightFromDesign( 126 );
    const rewards = this.getRewards().slice( 0, 4 );
    const icon_size = LayoutScaler.widthFromDesign( 96 );
    const plus_w = LayoutScaler.widthFromDesign( 34 );
    const cell_w = icon_size + plus_w;
    const row_w = rewards.length * icon_size + Math.max( 0, rewards.length - 1 ) * plus_w;
    const row_x = ( strip_w - button_w - LayoutScaler.widthFromDesign( 24 ) - row_w ) / 2;
    const icon_y = LayoutScaler.yFromDesign( 10 );

    rewards.forEach( ( reward, index ) =>
    {
      const x = row_x + index * cell_w;
      strip.addChild( new UIImage( this.ctx, x, icon_y, icon_size, icon_size, ContinueSpecialOfferPanel.rewardIcon( reward.type ) ) );
      strip.addChild( new UITextElement( this.ctx, x - LayoutScaler.xFromDesign( 12 ), icon_y + icon_size - LayoutScaler.yFromDesign( 10 ),
        icon_size + LayoutScaler.widthFromDesign( 24 ), LayoutScaler.heightFromDesign( 44 ), {
          text: ContinueSpecialOfferPanel.rewardLabel( reward ),
          font_size: LayoutScaler.textSizeFromDesign( 32 ), font_weight: 900,
          text_style: TextStyleScheme.rubik, text_color: ColorScheme.white,
          text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 7 ), outline_color: '#073985',
          auto_wrap: false, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 20 ),
        } ) );
      if ( index < rewards.length - 1 )
        strip.addChild( new UITextElement( this.ctx, x + icon_size, icon_y, plus_w, icon_size, {
          text: "+", font_size: LayoutScaler.textSizeFromDesign( 44 ), font_weight: 900,
          text_style: TextStyleScheme.rubik, text_color: ColorScheme.white,
          text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center, auto_wrap: false,
        } ) );
    } );

    const offer = this.params.offer;
    const rewarded = OfferService.isRewardedOffer( offer.id );
    const button_actual_h = rewarded ? button_w * ( 133 / 435 ) : button_h;
    const button = new UIImage(
      this.ctx, strip_w - button_w - LayoutScaler.widthFromDesign( 12 ), ( strip_h - button_actual_h ) / 2,
      button_w, button_actual_h, rewarded ? ImgID.UI_ChainOfferVideoIcon : ImgID.UI_ShopBtn, {}, true
    );
    if ( !rewarded && OfferService.isFreeOffer( offer.id ) ) button.setHueRotation( ContinueSpecialOfferPanel.FREE_BUTTON_HUE_ROTATION );
    strip.addChild( button );
    const label_x = rewarded ? button_w * 0.22 : 0;
    const outline_color = rewarded || OfferService.isFreeOffer( offer.id ) ? ColorScheme.purple_btn_outline : ColorScheme.menu_button_outline;
    this.price_text = new UITextElement( this.ctx, label_x, 0, button_w - label_x, button_actual_h, {
      text: "…", font_size: LayoutScaler.textSizeFromDesign( 40 ), font_weight: 900,
      text_style: TextStyleScheme.rubik, text_color: ColorScheme.white,
      text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: true, scale_to_width: true, min_font_size: LayoutScaler.textSizeFromDesign( 24 ), line_height: 0.9,
      outline_color, outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      ...TextStyleScheme.buttonShadow(),
    } );
    button.addChild( this.price_text, true );
    button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void this.purchase();
    } );
  }

  private static rewardIcon( type: OfferRewardDto['type'] ): ImgID
  {
    switch ( type )
    {
      case 'coins': return ImgID.IconCoin;
      case 'hearts': return ImgID.IconHeart;
      case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
      case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
      case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
      case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
      case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
      case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
      // case 'no_ads': case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
    }
  }

  private static rewardLabel( reward: OfferRewardDto ): string
  {
    if ( reward.type === 'level_shots' ) return `+${reward.amount}`;
    if ( reward.type === 'infinite_hearts_minutes' /* || reward.type === 'no_ads_minutes' */ ) return formatMinutesShort( reward.amount );
    // if ( reward.type === 'no_ads' ) return 'NO ADS';
    if ( reward.type === 'coins' ) return String( reward.amount );
    return `x${reward.amount}`;
  }

  private setPrice( text: string ): void
  {
    this.price_text?.setText( text );
    this.invalidateSubtree();
  }

  private async loadPrice(): Promise<void>
  {
    const offer = this.params.offer;
    if ( OfferService.isFreeOffer( offer.id ) || OfferService.isRewardedOffer( offer.id ) )
    {
      this.setPrice( offerButtonLabel( offer, "FREE" ) );
      return;
    }
    const product_id = OfferService.getPlatformProductId( offer.id );
    if ( !product_id ) { this.setPrice( "N/A" ); return; }
    try
    {
      const details = await ( await import( "../../../plugins/iap_plugin.js" ) ).IapPlugin.getProductDetails( product_id );
      this.setPrice( details?.formatted_price ?? offerButtonLabel( offer, "BUY" ) );
      const price_context = this.analyticsContext( {
        productid: product_id, formatted_price: details?.formatted_price, currency: details?.currency, price_micros: details?.price_micros,
      } );
      if ( details?.formatted_price ) await OfferService.trackPriceLoaded( offer.id, price_context );
      else await OfferService.trackPriceUnavailable( offer.id, { ...price_context, failure_reason: 'product_details_missing' } );
    }
    catch
    {
      this.setPrice( offerButtonLabel( offer, "BUY" ) );
      await OfferService.trackPriceUnavailable( offer.id, this.analyticsContext( { productid: product_id } ) );
    }
  }

  private async purchase(): Promise<void>
  {
    if ( this.purchase_in_progress ) return;
    this.purchase_in_progress = true;
    const offer = this.params.offer;
    const previous = this.price_text?.getText() ?? "";
    this.setPrice( "..." );
    try
    {
      await OfferService.trackClicked( offer.id, this.analyticsContext() );
      let result: ValidatePurchaseResponse | ClaimFreeOfferResponse;
      if ( OfferService.isRewardedOffer( offer.id ) )
      {
        const watched = await showRewarded( 'continue_offer' );
        await OfferService.trackRewardedAdOutcome( offer.id, watched, this.analyticsContext() );
        if ( !watched.rewarded ) { this.setPrice( previous ); return; }
        result = await OfferService.claimFreeOffer( offer.id, this.analyticsContext( { rewarded: true, rewarded_ad_id: watched.ad_id } ), { ad_id: watched.ad_id } );
      }
      else if ( OfferService.isFreeOffer( offer.id ) )
        result = await OfferService.claimFreeOffer( offer.id, this.analyticsContext() );
      else
        result = await OfferService.purchaseOffer( offer.id, this.analyticsContext() );

      if ( result.player ) adoptEconomyFromPlayer( result.player );
      else await refreshPlayerFromServer();
      this.purchased = true;
      // Shots are not inventory: whatever the server echoed goes straight into
      // the attempt the player is about to resume.
      this.params.on_purchased( result.granted?.level_shots ?? 0 );
    }
    catch ( error )
    {
      // A crypto payment still confirming on-chain: not a failure. The tracker grants the shots to the
      // account later; this attempt cannot wait for the chain, so the button simply returns to its label.
      if ( !( error instanceof TonPaymentPending ) ) console.warn( '[ContinueOffer] purchase failed', error );
      this.setPrice( previous );
    }
    finally
    {
      this.purchase_in_progress = false;
    }
  }
}
